import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useUnit } from 'effector-react';
import { createBuildingFx } from '../../store/buildings';
import AddressAutocompleteField from '../AddressAutocompleteField';

export default function AddBuildingDialog({ open, onClose, complexId, onSuccess, settlement = false }) {
  const pending = useUnit(createBuildingFx.pending);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [error, setError] = useState('');
  const [manualEnabled, setManualEnabled] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [mobileEnabled, setMobileEnabled] = useState(false);
  const [mobileViewOnly, setMobileViewOnly] = useState(false);
  const [allowAccount, setAllowAccount] = useState(false);
  const [allowCode, setAllowCode] = useState(false);
  const [allowMeterPair, setAllowMeterPair] = useState(false);
  const [instructions, setInstructions] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
      setAddress('');
      setLatitude(null);
      setLongitude(null);
      setError('');
      setManualEnabled(false);
      setViewOnly(false);
      setMobileEnabled(false);
      setMobileViewOnly(false);
      setAllowAccount(false);
      setAllowCode(false);
      setAllowMeterPair(false);
      setInstructions('');
    }
  }, [open]);

  const canSubmit = address.trim().length > 0 && (settlement || name.trim().length > 0);

  const applyGeoFromSuggestion = (suggestion) => {
    const lat = Number(suggestion?.data?.geo_lat);
    const lon = Number(suggestion?.data?.geo_lon);
    setLatitude(Number.isFinite(lat) ? lat : null);
    setLongitude(Number.isFinite(lon) ? lon : null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!canSubmit || pending) return;
    try {
      const { building } = await createBuildingFx({
        complexId: String(complexId),
        name: name.trim() || address.trim(),
        address: address.trim(),
        latitude,
        longitude,
        manual_view_only: viewOnly,
        manual_submission_enabled: viewOnly ? false : manualEnabled,
        mobile_view_only: mobileViewOnly,
        mobile_submission_enabled: mobileViewOnly ? false : mobileEnabled,
        manual_allow_account_registration: allowAccount,
        manual_allow_code_registration: allowCode,
        manual_allow_meter_pair_registration: allowMeterPair,
        manual_submission_instructions: instructions.trim() || null,
      });
      onSuccess?.(building);
      onClose?.();
    } catch (err) {
      setError('Не удалось создать дом. Проверьте данные и попробуйте снова.');
    }
  };

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{settlement ? 'Добавить дом в посёлок' : 'Добавить дом'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="Название (опционально)"
              placeholder={settlement ? 'Например: Дом 12' : 'Например: Корпус 1'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <AddressAutocompleteField
              label="Адрес"
              placeholder="Город, улица, дом..."
              value={address}
              onValueChange={(value) => {
                setAddress(value);
                setLatitude(null);
                setLongitude(null);
              }}
              onSuggestionSelect={(suggestion) => {
                if (suggestion?.value) {
                  setAddress(suggestion.value);
                }
                applyGeoFromSuggestion(suggestion);
              }}
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={viewOnly}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setViewOnly(checked);
                    if (checked) {
                      setManualEnabled(false);
                    }
                  }}
                />
              }
              label="Только просмотр (без передачи)"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={manualEnabled}
                  onChange={(e) => setManualEnabled(e.target.checked)}
                  disabled={viewOnly}
                />
              }
              label="Разрешить передачу показаний через Telegram"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={mobileViewOnly}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setMobileViewOnly(checked);
                    if (checked) {
                      setMobileEnabled(false);
                    }
                  }}
                />
              }
              label="Только просмотр в мобильном приложении"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={mobileEnabled}
                  onChange={(e) => setMobileEnabled(e.target.checked)}
                  disabled={mobileViewOnly}
                />
              }
              label="Разрешить передачу через мобильное приложение"
            />
            {manualEnabled && (
              <Stack spacing={1} sx={{ pl: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowAccount}
                      onChange={(e) => setAllowAccount(e.target.checked)}
                    />
                  }
                  label="Регистрация по лицевому счёту"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowCode}
                      onChange={(e) => setAllowCode(e.target.checked)}
                    />
                  }
                  label="Регистрация по одноразовому коду"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowMeterPair}
                      onChange={(e) => setAllowMeterPair(e.target.checked)}
                    />
                  }
                  label={settlement ? 'Регистрация по паре дом + счётчик' : 'Регистрация по паре квартира + счётчик'}
                />
                <TextField
                  label={settlement ? 'Инструкция для владельцев' : 'Инструкция для жильцов'}
                  multiline
                  minRows={3}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={pending}>
            Отмена
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit || pending}>
            {pending ? 'Сохранение...' : 'Создать'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
